import React, { useState, useEffect, useRef } from 'react';
import './PlotModal.css';
import { createPaidAiIdempotencyKey } from '../utils/api';

interface PlotPage {
  pageNumber: number;
  text: string;
}

interface PlotModalProps {
  onClose: () => void;
  onApply: (plots: PlotPage[], plotData?: any) => void;
  onApplyStructuredPlot?: (plotData: any) => void;
  onGeneratePage?: (pageData: any, options?: { replaceExisting?: boolean }) => Promise<boolean>;
  onGenerateAllPages?: (pages: any[], options?: { replaceExisting?: boolean }) => Promise<unknown>;
  onGenerateCharacter?: (name: string, prompt: string, options?: { replaceExisting?: boolean }) => Promise<string | undefined>;
  onGenerateBackground?: (name: string, prompt: string, options?: { replaceExisting?: boolean }) => Promise<string | undefined>;
  hasApiAccess: boolean;
  storyType?: string;
  storyTone?: string;
  storyStylePrompt?: string;
  visualStylePrompt?: string;
  dialogueStyle?: 'prose' | 'script';
  drawingType?: string;
  drawingStyle?: string;
  description?: string;
  lastStoryPrompt?: string;
  onSaveStoryPrompt?: (prompt: string) => void;
  existingPages?: number;
  savedPlots?: PlotPage[];
  structuredPlotData?: any;
  currentPageIndex?: number;
  onAddToPage?: (pageNumber: number, text: string, canvasWidth?: number, canvasHeight?: number) => void;
  canvasWidth?: number;
  canvasHeight?: number;
  defaultFontStyle?: 'normal' | 'italic';
  onFontStyleChange?: (fontStyle: 'normal' | 'italic') => void;
  onOpenFontSettings?: () => void;
  onOpenStyleSettings?: () => void;
  onOpenDesignSettings?: () => void;
  backgrounds?: Array<{ name: string; description?: string }>;
  onUpdateStructuredPlot?: (plotData: any) => void;
  user?: any;
  singlePageIndex?: number | null;
  singlePageNumber?: number; // The actual page number (1-indexed) for display and generation
  existingCharacters?: Array<{ name: string; imageUrl?: string; description?: string }>;
  existingBackgrounds?: Array<{ name: string; imageUrl?: string; description?: string }>;
  // Page-level AI data for regeneration (persists through page reordering)
  currentPageAiData?: {
    scenePrompt?: string;
    characterNames?: string[];
    backgroundName?: string;
    textBoxes?: Array<{ type: string; character?: string; content: string }>;
  };
  // Callback to save page AI data back to the page
  onSavePageAiData?: (aiData: {
    scenePrompt?: string;
    characterNames?: string[];
    backgroundName?: string;
    textBoxes?: Array<{ type: string; character?: string; content: string }>;
  }) => void;
}

const PlotModal: React.FC<PlotModalProps> = ({
  onClose,
  onApply,
  onApplyStructuredPlot,
  onGeneratePage,
  onGenerateAllPages,
  onGenerateCharacter,
  onGenerateBackground,
  hasApiAccess,
  storyType,
  storyTone,
  storyStylePrompt,
  visualStylePrompt,
  dialogueStyle = 'prose',
  drawingType,
  drawingStyle,
  description,
  lastStoryPrompt,
  onSaveStoryPrompt,
  existingPages = 1,
  savedPlots,
  structuredPlotData: initialStructuredPlotData,
  currentPageIndex = 0,
  onAddToPage,
  canvasWidth = 800,
  canvasHeight = 500,
  defaultFontStyle = 'normal',
  onFontStyleChange,
  onOpenFontSettings,
  onOpenStyleSettings,
  onOpenDesignSettings,
  backgrounds = [],
  onUpdateStructuredPlot,
  user,
  singlePageIndex = null,
  singlePageNumber,
  existingCharacters = [],
  existingBackgrounds = [],
  currentPageAiData,
  onSavePageAiData
}) => {
  const isSinglePageMode = singlePageIndex !== null;
  const [numPages, setNumPages] = useState(savedPlots?.length || existingPages || 10);
  const [plots, setPlots] = useState<PlotPage[]>(
    savedPlots && savedPlots.length > 0
      ? savedPlots
      : Array.from({ length: existingPages }, (_, i) => ({
          pageNumber: i + 1,
          text: ''
        }))
  );
  const [aiPrompt, setAiPrompt] = useState(lastStoryPrompt || description || '');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatingPage, setGeneratingPage] = useState<number | null>(null);
  const [generationProgress, setGenerationProgress] = useState(0);
  const [structuredPlotData, setStructuredPlotData] = useState<any>(initialStructuredPlotData);
  const [viewMode, setViewMode] = useState<'simple' | 'structured'>(initialStructuredPlotData ? 'structured' : 'simple');
  const [fontStyle, setFontStyle] = useState<'normal' | 'italic'>(defaultFontStyle);
  const [selectedPages, setSelectedPages] = useState<Set<number>>(new Set());
  
  // State for edited page AI data in single-page mode (preserves edits during regeneration)
  const [editedPageAiData, setEditedPageAiData] = useState<{
    scenePrompt?: string;
    characterNames?: string[];
    backgroundName?: string;
    textBoxes?: Array<{ type: string; character?: string; content: string }>;
  } | null>(currentPageAiData || null);
  
  // State for single-page prompt input (when page has no AI data)
  const [singlePagePrompt, setSinglePagePrompt] = useState('');
  const [isGeneratingSinglePagePlot, setIsGeneratingSinglePagePlot] = useState(false);
  
  // State for batch character/background generation
  const [generatingCharacters, setGeneratingCharacters] = useState(false);
  const [generatingCharacterIndex, setGeneratingCharacterIndex] = useState<number | null>(null);
  const [generatingCharacterTotal, setGeneratingCharacterTotal] = useState<number>(0);
  const [generatingBackgrounds, setGeneratingBackgrounds] = useState(false);
  const [generatingBackgroundIndex, setGeneratingBackgroundIndex] = useState<number | null>(null);
  const [generatingBackgroundTotal, setGeneratingBackgroundTotal] = useState<number>(0);
  
  // State for character/background selection (for regeneration)
  const [selectedCharacters, setSelectedCharacters] = useState<Set<string>>(new Set());
  const [selectedBackgroundsSet, setSelectedBackgroundsSet] = useState<Set<string>>(new Set());
  
  // Sync structuredPlotData and viewMode when props change (e.g., when modal reopens)
  useEffect(() => {
    setStructuredPlotData(initialStructuredPlotData);
    if (initialStructuredPlotData) {
      setViewMode('structured');
    }
  }, [initialStructuredPlotData]);

  // Initialize editedPageAiData only when modal first opens in single-page mode
  // Use a ref to track if we've already initialized to prevent resetting on prop changes
  const hasInitializedEditedData = useRef(false);
  useEffect(() => {
    if (isSinglePageMode && currentPageAiData && !hasInitializedEditedData.current) {
      setEditedPageAiData(currentPageAiData);
      hasInitializedEditedData.current = true;
    }
    // Reset the ref when modal closes (isSinglePageMode becomes false)
    if (!isSinglePageMode) {
      hasInitializedEditedData.current = false;
    }
  }, [isSinglePageMode, currentPageAiData]);
  
  const handleUpdatePageBackground = (pageIndex: number, backgroundName: string) => {
    // In single-page mode, update the local edited state (pageIndex will be 0 from the mapped array)
    if (isSinglePageMode) {
      setEditedPageAiData(prev => ({
        ...prev,
        backgroundName
      }));
    }
    // Also update structuredPlotData if it exists (use singlePageIndex for actual position)
    const actualIndex = isSinglePageMode ? singlePageIndex : pageIndex;
    if (structuredPlotData?.pages?.[actualIndex!]) {
      const updatedPages = [...structuredPlotData.pages];
      updatedPages[actualIndex!] = { ...updatedPages[actualIndex!], backgroundName };
      const updatedData = { ...structuredPlotData, pages: updatedPages };
      setStructuredPlotData(updatedData);
      onUpdateStructuredPlot?.(updatedData);
    }
  };
  
  const handleUpdateScenePrompt = (pageIndex: number, scenePrompt: string) => {
    // In single-page mode, update the local edited state (pageIndex will be 0 from the mapped array)
    if (isSinglePageMode) {
      setEditedPageAiData(prev => ({
        ...prev,
        scenePrompt
      }));
    }
    // Also update structuredPlotData if it exists (use singlePageIndex for actual position)
    const actualIndex = isSinglePageMode ? singlePageIndex : pageIndex;
    if (structuredPlotData?.pages?.[actualIndex!]) {
      const updatedPages = [...structuredPlotData.pages];
      updatedPages[actualIndex!] = { ...updatedPages[actualIndex!], scenePrompt };
      const updatedData = { ...structuredPlotData, pages: updatedPages };
      setStructuredPlotData(updatedData);
      onUpdateStructuredPlot?.(updatedData);
    }
  };
  
  const handleUpdateTextBoxContent = (pageIndex: number, textBoxIndex: number, content: string) => {
    // In single-page mode, update the local edited state (pageIndex will be 0 from the mapped array)
    if (isSinglePageMode) {
      setEditedPageAiData(prev => {
        const currentData = prev || { textBoxes: [] };
        const updatedTextBoxes = [...(currentData.textBoxes || [])];
        if (updatedTextBoxes[textBoxIndex]) {
          updatedTextBoxes[textBoxIndex] = { ...updatedTextBoxes[textBoxIndex], content };
        }
        return { ...currentData, textBoxes: updatedTextBoxes };
      });
    }
    // Also update structuredPlotData if it exists (use singlePageIndex for actual position)
    const actualIndex = isSinglePageMode ? singlePageIndex : pageIndex;
    if (structuredPlotData?.pages?.[actualIndex!]) {
      const updatedPages = [...structuredPlotData.pages];
      const updatedTextBoxes = [...updatedPages[actualIndex!].textBoxes];
      updatedTextBoxes[textBoxIndex] = { ...updatedTextBoxes[textBoxIndex], content };
      updatedPages[actualIndex!] = { ...updatedPages[actualIndex!], textBoxes: updatedTextBoxes };
      const updatedData = { ...structuredPlotData, pages: updatedPages };
      setStructuredPlotData(updatedData);
      onUpdateStructuredPlot?.(updatedData);
    }
  };

  const handleUpdateCharacterDescription = (charIndex: number, description: string) => {
    const updatedCharacters = [...structuredPlotData.characters];
    updatedCharacters[charIndex] = { ...updatedCharacters[charIndex], description };
    const updatedData = { ...structuredPlotData, characters: updatedCharacters };
    setStructuredPlotData(updatedData);
    onUpdateStructuredPlot?.(updatedData);
  };

  const handleUpdateBackgroundDescription = (bgIndex: number, description: string) => {
    const updatedBackgrounds = [...structuredPlotData.backgrounds];
    updatedBackgrounds[bgIndex] = { ...updatedBackgrounds[bgIndex], description };
    const updatedData = { ...structuredPlotData, backgrounds: updatedBackgrounds };
    setStructuredPlotData(updatedData);
    onUpdateStructuredPlot?.(updatedData);
  };

  const handleDeletePlotPage = (pageIndex: number) => {
    if (!structuredPlotData?.pages || structuredPlotData.pages.length <= 1) {
      alert('Cannot delete the last page.');
      return;
    }
    
    if (!window.confirm(`Are you sure you want to delete Page ${pageIndex + 1}? This cannot be undone.`)) {
      return;
    }
    
    const updatedPages = structuredPlotData.pages.filter((_: any, idx: number) => idx !== pageIndex);
    const updatedData = { ...structuredPlotData, pages: updatedPages };
    setStructuredPlotData(updatedData);
    onUpdateStructuredPlot?.(updatedData);
    
    // Also remove from selection if selected
    setSelectedPages(prev => {
      const newSet = new Set<number>();
      prev.forEach(idx => {
        if (idx < pageIndex) {
          newSet.add(idx);
        } else if (idx > pageIndex) {
          newSet.add(idx - 1); // Shift down
        }
        // Skip if idx === pageIndex (deleted)
      });
      return newSet;
    });
  };
  
  const estimatePageTokens = (page: any): number => {
    // Estimate tokens for a single page generation using actual token costs
    // Character generation: 20 tokens per character (GEMINI)
    const charTokens = (page.characterNames?.length || 0) * 20;
    // Background generation: 20 tokens (GEMINI)
    const bgTokens = page.backgroundName ? 20 : 0;
    // Scene composition: 20 tokens (GEMINI)
    const sceneTokens = (page.characterNames?.length || 0) > 0 || page.backgroundName ? 20 : 0;
    // Vision analysis: 4 tokens (PROMPT)
    const visionTokens = 4;
    
    return charTokens + bgTokens + sceneTokens + visionTokens;
  };

  const estimateAllPagesTokens = (): number => {
    if (!structuredPlotData?.pages) return 0;
    return structuredPlotData.pages.reduce((total: number, page: any) => total + estimatePageTokens(page), 0);
  };

  const hasEnoughTokens = (requiredTokens: number): boolean => {
    if (!user) return false;
    const totalTokens = (user.paidTokens || 0) + (user.monthlyCredits || 0);
    return totalTokens >= requiredTokens;
  };

  // Page selection helpers
  const togglePageSelection = (pageIndex: number) => {
    setSelectedPages(prev => {
      const newSet = new Set(prev);
      if (newSet.has(pageIndex)) {
        newSet.delete(pageIndex);
      } else {
        newSet.add(pageIndex);
      }
      return newSet;
    });
  };

  const selectAllPages = () => {
    if (structuredPlotData?.pages) {
      setSelectedPages(new Set(structuredPlotData.pages.map((_: any, idx: number) => idx)));
    }
  };

  const deselectAllPages = () => {
    setSelectedPages(new Set());
  };

  const getSelectedPagesTokens = (): number => {
    if (!structuredPlotData?.pages) return 0;
    return Array.from(selectedPages).reduce((total, idx) => {
      const page = structuredPlotData.pages[idx];
      return total + (page ? estimatePageTokens(page) : 0);
    }, 0);
  };

  // Check if a character already exists in the storybook
  const characterExists = (charName: string): boolean => {
    return existingCharacters.some(c => c.name === charName && c.imageUrl);
  };

  // Check if a background already exists in the storybook
  const backgroundExists = (bgName: string): boolean => {
    return existingBackgrounds.some(b => b.name === bgName && b.imageUrl);
  };

  // Get characters that need to be generated
  const getMissingCharacters = () => {
    if (!structuredPlotData?.characters) return [];
    return structuredPlotData.characters.filter((char: any) => !characterExists(char.name));
  };

  // Get backgrounds that need to be generated
  const getMissingBackgrounds = () => {
    if (!structuredPlotData?.backgrounds) return [];
    return structuredPlotData.backgrounds.filter((bg: any) => !backgroundExists(bg.name));
  };

  // Handle generating all missing characters
  const handleGenerateAllCharacters = async () => {
    if (!onGenerateCharacter) return;
    
    const missingChars = getMissingCharacters();
    if (missingChars.length === 0) {
      alert('All characters have already been generated!');
      return;
    }

    const tokensNeeded = missingChars.length * 20; // 20 tokens per character
    if (!hasEnoughTokens(tokensNeeded)) {
      alert(`Not enough tokens. Need ${tokensNeeded} tokens to generate ${missingChars.length} characters.`);
      return;
    }

    setGeneratingCharacters(true);
    setGeneratingCharacterTotal(missingChars.length);
    try {
      for (let i = 0; i < missingChars.length; i++) {
        const char = missingChars[i];
        setGeneratingCharacterIndex(i);
        try {
          await onGenerateCharacter(char.name, char.description || char.name);
        } catch (error) {
          console.error(`Failed to generate character ${char.name}:`, error);
        }
      }
    } finally {
      setGeneratingCharacters(false);
      setGeneratingCharacterIndex(null);
      setGeneratingCharacterTotal(0);
    }
  };

  // Handle generating all missing backgrounds
  const handleGenerateAllBackgrounds = async () => {
    if (!onGenerateBackground) return;
    
    const missingBgs = getMissingBackgrounds();
    if (missingBgs.length === 0) {
      alert('All backgrounds have already been generated!');
      return;
    }

    const tokensNeeded = missingBgs.length * 20; // 20 tokens per background
    if (!hasEnoughTokens(tokensNeeded)) {
      alert(`Not enough tokens. Need ${tokensNeeded} tokens to generate ${missingBgs.length} backgrounds.`);
      return;
    }

    setGeneratingBackgrounds(true);
    setGeneratingBackgroundTotal(missingBgs.length);
    try {
      for (let i = 0; i < missingBgs.length; i++) {
        const bg = missingBgs[i];
        setGeneratingBackgroundIndex(i);
        try {
          await onGenerateBackground(bg.name, bg.description || bg.name);
        } catch (error) {
          console.error(`Failed to generate background ${bg.name}:`, error);
        }
      }
    } finally {
      setGeneratingBackgrounds(false);
      setGeneratingBackgroundIndex(null);
      setGeneratingBackgroundTotal(0);
    }
  };

  // Character selection helpers
  const toggleCharacterSelection = (charName: string) => {
    setSelectedCharacters(prev => {
      const newSet = new Set(prev);
      if (newSet.has(charName)) {
        newSet.delete(charName);
      } else {
        newSet.add(charName);
      }
      return newSet;
    });
  };

  const selectAllCharacters = () => {
    if (structuredPlotData?.characters) {
      const existingCharNames = structuredPlotData.characters
        .filter((char: any) => characterExists(char.name))
        .map((char: any) => char.name);
      setSelectedCharacters(new Set(existingCharNames));
    }
  };

  const deselectAllCharacters = () => {
    setSelectedCharacters(new Set());
  };

  // Background selection helpers
  const toggleBackgroundSelection = (bgName: string) => {
    setSelectedBackgroundsSet(prev => {
      const newSet = new Set(prev);
      if (newSet.has(bgName)) {
        newSet.delete(bgName);
      } else {
        newSet.add(bgName);
      }
      return newSet;
    });
  };

  const selectAllBackgroundsForRegen = () => {
    if (structuredPlotData?.backgrounds) {
      const existingBgNames = structuredPlotData.backgrounds
        .filter((bg: any) => backgroundExists(bg.name))
        .map((bg: any) => bg.name);
      setSelectedBackgroundsSet(new Set(existingBgNames));
    }
  };

  const deselectAllBackgroundsForRegen = () => {
    setSelectedBackgroundsSet(new Set());
  };

  // Handle regenerating selected characters
  const handleRegenerateSelectedCharacters = async () => {
    if (!onGenerateCharacter || selectedCharacters.size === 0) return;
    
    const charsToRegen = structuredPlotData?.characters?.filter((char: any) => selectedCharacters.has(char.name)) || [];
    if (charsToRegen.length === 0) return;

    const tokensNeeded = charsToRegen.length * 20;
    if (!hasEnoughTokens(tokensNeeded)) {
      alert(`Not enough tokens. Need ${tokensNeeded} tokens to regenerate ${charsToRegen.length} characters.`);
      return;
    }

    setGeneratingCharacters(true);
    setGeneratingCharacterTotal(charsToRegen.length);
    try {
      for (let i = 0; i < charsToRegen.length; i++) {
        const char = charsToRegen[i];
        setGeneratingCharacterIndex(i);
        try {
          await onGenerateCharacter(char.name, char.description || char.name, { replaceExisting: true });
        } catch (error) {
          console.error(`Failed to regenerate character ${char.name}:`, error);
        }
      }
      deselectAllCharacters();
    } finally {
      setGeneratingCharacters(false);
      setGeneratingCharacterIndex(null);
      setGeneratingCharacterTotal(0);
    }
  };

  // Handle regenerating selected backgrounds
  const handleRegenerateSelectedBackgrounds = async () => {
    if (!onGenerateBackground || selectedBackgroundsSet.size === 0) return;
    
    const bgsToRegen = structuredPlotData?.backgrounds?.filter((bg: any) => selectedBackgroundsSet.has(bg.name)) || [];
    if (bgsToRegen.length === 0) return;

    const tokensNeeded = bgsToRegen.length * 20;
    if (!hasEnoughTokens(tokensNeeded)) {
      alert(`Not enough tokens. Need ${tokensNeeded} tokens to regenerate ${bgsToRegen.length} backgrounds.`);
      return;
    }

    setGeneratingBackgrounds(true);
    setGeneratingBackgroundTotal(bgsToRegen.length);
    try {
      for (let i = 0; i < bgsToRegen.length; i++) {
        const bg = bgsToRegen[i];
        setGeneratingBackgroundIndex(i);
        try {
          await onGenerateBackground(bg.name, bg.description || bg.name, { replaceExisting: true });
        } catch (error) {
          console.error(`Failed to regenerate background ${bg.name}:`, error);
        }
      }
      deselectAllBackgroundsForRegen();
    } finally {
      setGeneratingBackgrounds(false);
      setGeneratingBackgroundIndex(null);
      setGeneratingBackgroundTotal(0);
    }
  };

  // Handle generating single-page plot (for pages without AI data)
  const handleGenerateSinglePagePlot = async () => {
    if (!singlePagePrompt.trim()) {
      alert('Please enter a prompt for this page');
      return;
    }

    setIsGeneratingSinglePagePlot(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/ai/generate-single-page-plot`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Idempotency-Key': createPaidAiIdempotencyKey()
        },
        body: JSON.stringify({
          prompt: singlePagePrompt,
          pageNumber: singlePageNumber || 1,
          storyType: storyType || 'Fairy Tales',
          storyTone: storyTone || 'Playful',
          storyStylePrompt: storyStylePrompt,
          visualStylePrompt: visualStylePrompt,
          drawingType: drawingType,
          drawingStyle: drawingStyle,
          existingCharacters: existingCharacters.map(c => ({ name: c.name, description: c.description })),
          existingBackgrounds: existingBackgrounds.map(b => ({ name: b.name, description: b.description }))
        })
      });

      if (!response.ok) {
        throw new Error('Failed to generate page plot');
      }

      const data = await response.json();
      
      if (data.success && data.pageData) {
        console.log('✅ Generated single page plot:', data.pageData);
        
        // Update local state
        const newAiData = {
          scenePrompt: data.pageData.scenePrompt || '',
          characterNames: data.pageData.characterNames || [],
          backgroundName: data.pageData.backgroundName || '',
          textBoxes: data.pageData.textBoxes || []
        };
        setEditedPageAiData(newAiData);
        
        // Save to page via callback
        onSavePageAiData?.(newAiData);
      }
    } catch (error) {
      console.error('Failed to generate single page plot:', error);
      alert('Failed to generate page content. Please try again.');
    } finally {
      setIsGeneratingSinglePagePlot(false);
    }
  };

  // Auto-save plots whenever they change (debounced to avoid excessive saves)
  useEffect(() => {
    // Only auto-save if there's actual content (not just on initial mount)
    const hasContent = plots.some(p => p.text.trim().length > 0);
    if (!hasContent) return;

    // Debounce auto-save by 2 seconds to avoid saving on every keystroke
    const debounceTimer = setTimeout(() => {
      onApply(plots, structuredPlotData);
    }, 2000);

    return () => clearTimeout(debounceTimer);
  }, [plots, structuredPlotData]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleNumPagesChange = (num: number) => {
    setNumPages(num);
    const newPlots = Array.from({ length: num }, (_, i) => ({
      pageNumber: i + 1,
      text: plots[i]?.text || ''
    }));
    setPlots(newPlots);
  };

  const handlePlotChange = (pageNumber: number, text: string) => {
    setPlots(plots.map(p => 
      p.pageNumber === pageNumber ? { ...p, text } : p
    ));
  };

  const handleGenerateAll = async () => {
    if (!aiPrompt.trim()) {
      alert('Please enter a story prompt');
      return;
    }

    // Validate numPages
    const pagesToGenerate = numPages || 10;
    if (pagesToGenerate < 1 || pagesToGenerate > 200) {
      alert('Number of pages must be between 1 and 200');
      return;
    }

    setIsGenerating(true);
    setGenerationProgress(0);
    
    // Simulate progress over ~60 seconds
    const progressInterval = setInterval(() => {
      setGenerationProgress(prev => {
        if (prev >= 95) return prev; // Stop at 95% until actual completion
        return prev + (100 / 120); // Increment to reach ~95% in 60 seconds (120 intervals of 500ms)
      });
    }, 500);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/ai/generate-plot`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Idempotency-Key': createPaidAiIdempotencyKey()
        },
        body: JSON.stringify({
          prompt: aiPrompt,
          numPages: pagesToGenerate,
          storyType: storyType || 'Fairy Tales',
          storyTone: storyTone || 'Playful',
          storyStylePrompt: storyStylePrompt,
          visualStylePrompt: visualStylePrompt,
          dialogueStyle: dialogueStyle,
          drawingType: drawingType,
          drawingStyle: drawingStyle
        })
      });

      if (!response.ok) {
        throw new Error('Failed to generate plot');
      }

      const data = await response.json();
      
      // Handle new structured format with characters, backgrounds, and detailed pages
      if (data.success && data.plotData) {
        console.log('✅ Received structured plot data:', data.plotData);
        setStructuredPlotData(data.plotData);
        setViewMode('structured');
        
        // Save the story prompt for future use
        onSaveStoryPrompt?.(aiPrompt);
        
        // Also convert to simple format for backward compatibility
        const simplePlots = data.plotData.pages.map((page: any, index: number) => {
          const textContent = page.textBoxes
            .map((tb: any) => {
              if (tb.type === 'dialogue' && tb.character) {
                return `${tb.character}: "${tb.content}"`;
              }
              return tb.content;
            })
            .join('\n\n');
          
          return {
            pageNumber: index + 1,
            text: textContent
          };
        });
        
        setPlots(simplePlots);
      } 
      // Handle old format for backward compatibility
      else if (data.success && data.plots) {
        setPlots(data.plots.map((text: string, index: number) => ({
          pageNumber: index + 1,
          text
        })));
        setViewMode('simple');
        
        // Save the story prompt for future use
        onSaveStoryPrompt?.(aiPrompt);
      }
      
      clearInterval(progressInterval);
      setGenerationProgress(100);
      setTimeout(() => setGenerationProgress(0), 1000);
    } catch (error) {
      console.error('Failed to generate plot:', error);
      alert('Failed to generate plot. Please try again.');
      clearInterval(progressInterval);
      setGenerationProgress(0);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleGeneratePage = async (pageNumber: number) => {
    const currentPlot = plots.find(p => p.pageNumber === pageNumber);
    if (!currentPlot?.text.trim()) {
      alert('Please enter some text for context');
      return;
    }

    setGeneratingPage(pageNumber);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/ai/rephrase-plot`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Idempotency-Key': createPaidAiIdempotencyKey()
        },
        body: JSON.stringify({
          text: currentPlot.text,
          storyType: storyType || 'Fairy Tales',
          storyTone: storyTone || 'Playful',
          storyStylePrompt: storyStylePrompt,
          visualStylePrompt: visualStylePrompt
        })
      });

      if (!response.ok) {
        throw new Error('Failed to rephrase plot');
      }

      const data = await response.json();
      
      if (data.success && data.text) {
        handlePlotChange(pageNumber, data.text);
      }
    } catch (error) {
      console.error('Failed to rephrase plot:', error);
      alert('Failed to improve text. Please try again.');
    } finally {
      setGeneratingPage(null);
    }
  };

  const handleClose = () => {
    // Auto-save plots when closing
    onApply(plots, structuredPlotData);
    // Save the story prompt for future use
    if (aiPrompt && aiPrompt.trim()) {
      onSaveStoryPrompt?.(aiPrompt);
    }
    onClose();
  };

  const handleClearAll = () => {
    if (window.confirm('Are you sure you want to clear all plots? This cannot be undone.')) {
      const clearedPlots = plots.map(p => ({ ...p, text: '' }));
      setPlots(clearedPlots);
      setAiPrompt('');
      setStructuredPlotData(null);
      setViewMode('simple');
      // Immediately save the cleared state
      onApply(clearedPlots, null);
    }
  };

  const handleNumPagesInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === '') {
      setNumPages(1);
      return;
    }
    const num = parseInt(value, 10);
    if (!isNaN(num) && num >= 1 && num <= 200) {
      handleNumPagesChange(num);
    }
  };

  const handleInsertPageBefore = (pageNumber: number) => {
    const insertIndex = pageNumber - 1;
    const newPlots = [...plots];
    newPlots.splice(insertIndex, 0, { pageNumber, text: '' });
    const renumbered = newPlots.map((p, i) => ({ ...p, pageNumber: i + 1 }));
    setPlots(renumbered);
    setNumPages(renumbered.length);
  };

  const handleRemovePage = (pageNumber: number) => {
    if (plots.length <= 1) return;
    const newPlots = plots.filter(p => p.pageNumber !== pageNumber);
    const renumbered = newPlots.map((p, i) => ({ ...p, pageNumber: i + 1 }));
    setPlots(renumbered);
    setNumPages(renumbered.length);
  };

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content plot-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isSinglePageMode ? `Regenerate Page ${singlePageNumber || (singlePageIndex! + 1)}` : 'Story Plot Planning'}</h2>
          <button className="close-btn" onClick={handleClose}>×</button>
        </div>

        <div className="modal-body">
          {/* AI Generation Settings - Only show in full mode */}
          {!isSinglePageMode && (
            <div className="ai-generate-section" style={{
              marginBottom: '20px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              padding: '24px',
              borderRadius: '12px',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
            }}>
              <div style={{ marginBottom: '16px' }}>
                <h3 style={{ color: 'white', margin: '0 0 16px 0', fontSize: '18px', fontWeight: '600' }}>
                  🤖 AI-Powered Plot Generation
                </h3>
              </div>
              <div className="form-group" style={{ marginBottom: '16px' }}>
                <label style={{ color: 'white', fontWeight: '500', marginBottom: '8px', display: 'block' }}>Story Prompt:</label>
                <textarea
                  value={aiPrompt}
                  onChange={(e) => setAiPrompt(e.target.value)}
                  placeholder="Describe your story... (e.g., 'A little fox who goes on an adventure to find a magical treasure')"
                  className="form-control"
                  rows={3}
                  style={{
                    background: 'rgba(255, 255, 255, 0.95)',
                    border: '2px solid rgba(255, 255, 255, 0.3)',
                    borderRadius: '8px',
                    padding: '12px',
                    fontSize: '14px'
                  }}
                />
              </div>
              <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                <button
                  className="btn btn-primary"
                  onClick={handleGenerateAll}
                  disabled={isGenerating || !aiPrompt.trim()}
                  style={{
                    background: 'white',
                    color: '#667eea',
                    border: 'none',
                    padding: '10px 20px',
                    borderRadius: '8px',
                    fontWeight: '600',
                    cursor: isGenerating || !aiPrompt.trim() ? 'not-allowed' : 'pointer',
                    opacity: isGenerating || !aiPrompt.trim() ? 0.6 : 1,
                    transition: 'all 0.2s',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                >
                  {isGenerating ? (
                    <>
                      <span style={{ position: 'relative', zIndex: 1 }}>
                        🤖 Generating... {Math.round(generationProgress)}%
                      </span>
                      <div style={{
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        height: '100%',
                        width: `${generationProgress}%`,
                        backgroundColor: 'rgba(102, 126, 234, 0.2)',
                        transition: 'width 0.5s ease',
                        zIndex: 0
                      }} />
                    </>
                  ) : (
                    '🤖 Generate All Plots'
                  )}
                </button>
                <span style={{ fontSize: '11px', color: 'rgba(255, 255, 255, 0.9)', fontWeight: '500' }}>
                  ~4 tokens
                </span>
                {structuredPlotData && (
                  <button
                    className="btn btn-secondary"
                    onClick={() => setViewMode(viewMode === 'simple' ? 'structured' : 'simple')}
                    style={{
                      background: 'rgba(255, 255, 255, 0.2)',
                      color: 'white',
                      border: '2px solid rgba(255, 255, 255, 0.5)',
                      padding: '10px 20px',
                      borderRadius: '8px',
                      fontWeight: '500',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                  >
                    {viewMode === 'simple' ? '📊 Structured View' : '📝 Simple View'}
                  </button>
                )}
                {onOpenFontSettings && (
                  <button
                    type="button"
                    onClick={onOpenFontSettings}
                    style={{
                      background: 'rgba(255, 255, 255, 0.2)',
                      color: 'white',
                      border: '2px solid rgba(255, 255, 255, 0.5)',
                      padding: '10px 20px',
                      borderRadius: '8px',
                      fontWeight: '500',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                  >
                    <span>⚙️</span>
                    <span>Font Settings</span>
                  </button>
                )}
                {onOpenDesignSettings && (
                  <button
                    type="button"
                    onClick={onOpenDesignSettings}
                    style={{
                      background: 'rgba(255, 255, 255, 0.2)',
                      color: 'white',
                      border: '2px solid rgba(255, 255, 255, 0.5)',
                      padding: '10px 20px',
                      borderRadius: '8px',
                      fontWeight: '500',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                  >
                    <span>🎨</span>
                    <span>Design Settings</span>
                  </button>
                )}
                {onOpenStyleSettings && (
                  <button
                    type="button"
                    onClick={onOpenStyleSettings}
                    style={{
                      background: 'rgba(255, 255, 255, 0.2)',
                      color: 'white',
                      border: '2px solid rgba(255, 255, 255, 0.5)',
                      padding: '10px 20px',
                      borderRadius: '8px',
                      fontWeight: '500',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                  >
                    <span>📖</span>
                    <span>Story Settings</span>
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Single Page Prompt Input - Show when in single page mode with no AI data */}
          {isSinglePageMode && !editedPageAiData?.scenePrompt && !editedPageAiData?.textBoxes?.length && (
            <div className="ai-generate-section" style={{
              marginBottom: '20px',
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              padding: '24px',
              borderRadius: '12px',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
            }}>
              <div style={{ marginBottom: '16px' }}>
                <h3 style={{ color: 'white', margin: '0 0 8px 0', fontSize: '18px', fontWeight: '600' }}>
                  📝 Create Page Content
                </h3>
                <p style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '14px', margin: 0 }}>
                  This page doesn't have AI-generated content yet. Enter a prompt to generate scene and text content for this page.
                </p>
              </div>
              <div className="form-group" style={{ marginBottom: '16px' }}>
                <label style={{ color: 'white', fontWeight: '500', marginBottom: '8px', display: 'block' }}>Page Prompt:</label>
                <textarea
                  value={singlePagePrompt}
                  onChange={(e) => setSinglePagePrompt(e.target.value)}
                  placeholder={`What happens on page ${singlePageNumber || 1}? (e.g., 'The fox discovers a hidden cave in the forest')`}
                  className="form-control"
                  rows={3}
                  style={{
                    background: 'rgba(255, 255, 255, 0.95)',
                    border: '2px solid rgba(255, 255, 255, 0.3)',
                    borderRadius: '8px',
                    padding: '12px',
                    fontSize: '14px'
                  }}
                />
              </div>
              <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                <button
                  className="btn btn-primary"
                  onClick={handleGenerateSinglePagePlot}
                  disabled={isGeneratingSinglePagePlot || !singlePagePrompt.trim()}
                  style={{
                    background: 'white',
                    color: '#059669',
                    border: 'none',
                    padding: '10px 20px',
                    borderRadius: '8px',
                    fontWeight: '600',
                    cursor: isGeneratingSinglePagePlot || !singlePagePrompt.trim() ? 'not-allowed' : 'pointer',
                    opacity: isGeneratingSinglePagePlot || !singlePagePrompt.trim() ? 0.6 : 1,
                    transition: 'all 0.2s',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                  }}
                >
                  {isGeneratingSinglePagePlot ? '🤖 Generating...' : '🤖 Generate Page Content'}
                </button>
                <span style={{ fontSize: '11px', color: 'rgba(255, 255, 255, 0.9)', fontWeight: '500' }}>
                  ~4 tokens
                </span>
              </div>
            </div>
          )}

          {/* Plot settings - Only show in full mode */}
          {!isSinglePageMode && (
            <div className="plot-settings">
              <div className="form-group">
                <label>Number of Content Pages:</label>
                <input
                  type="number"
                  min="1"
                  max="200"
                  value={numPages}
                  onChange={handleNumPagesInputChange}
                  className="form-control"
                />
                <small style={{ fontSize: '11px', color: '#6B7280', marginTop: '4px', display: 'block' }}>
                  (Title cover and Back cover are not counted)
                </small>
              </div>
            </div>
          )}

          {/* Structured Plot Data View - Show for structured view OR single page mode */}
          {((structuredPlotData && viewMode === 'structured') || isSinglePageMode) && (
            <div className="structured-plot-view">
              {/* Characters section - Only show in full mode */}
              {!isSinglePageMode && structuredPlotData && (
                <div className="plot-section">
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '15px', flexWrap: 'wrap', gap: '10px' }}>
                    <h3 style={{ margin: 0 }}>📚 Characters ({structuredPlotData.characters?.length || 0})</h3>
                    {onGenerateCharacter && structuredPlotData.characters?.length > 0 && (() => {
                      const missingChars = getMissingCharacters();
                      const tokensNeeded = missingChars.length * 20;
                      const hasTokens = hasEnoughTokens(tokensNeeded);
                      const allGenerated = missingChars.length === 0;
                      return (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          {!allGenerated && (
                            <span style={{ fontSize: '11px', color: hasTokens ? '#6B7280' : '#DC2626', fontWeight: '500' }}>
                              ~{tokensNeeded} tokens ({missingChars.length} to generate)
                            </span>
                          )}
                          <button
                            className="btn btn-primary"
                            onClick={handleGenerateAllCharacters}
                            disabled={generatingCharacters || allGenerated || !hasTokens}
                            style={{
                              padding: '8px 16px',
                              fontSize: '14px',
                              fontWeight: '600',
                              background: allGenerated 
                                ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)' 
                                : 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                              opacity: (generatingCharacters || (!allGenerated && !hasTokens)) ? 0.5 : 1,
                              cursor: (generatingCharacters || allGenerated || !hasTokens) ? 'not-allowed' : 'pointer'
                            }}
                          >
                            {generatingCharacters 
                              ? `🎨 Generating ${(generatingCharacterIndex || 0) + 1}/${generatingCharacterTotal}...`
                              : allGenerated 
                                ? '✅ All Generated' 
                                : `🎨 Generate All Characters (${missingChars.length})`}
                          </button>
                        </div>
                      );
                    })()}
                  </div>
                  {/* Selection controls for regeneration */}
                  {onGenerateCharacter && structuredPlotData.characters?.some((char: any) => characterExists(char.name)) && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px', flexWrap: 'wrap' }}>
                      <button
                        onClick={selectedCharacters.size === structuredPlotData.characters.filter((c: any) => characterExists(c.name)).length ? deselectAllCharacters : selectAllCharacters}
                        style={{
                          padding: '4px 10px',
                          fontSize: '11px',
                          fontWeight: '500',
                          background: 'transparent',
                          border: '1px solid #d1d5db',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          color: '#374151'
                        }}
                      >
                        {selectedCharacters.size === structuredPlotData.characters.filter((c: any) => characterExists(c.name)).length ? '☐ Deselect All' : '☑ Select All'}
                      </button>
                      {selectedCharacters.size > 0 && (
                        <>
                          <span style={{ fontSize: '11px', color: '#6B7280' }}>
                            {selectedCharacters.size} selected
                          </span>
                          <span style={{ fontSize: '11px', color: hasEnoughTokens(selectedCharacters.size * 20) ? '#6B7280' : '#DC2626' }}>
                            (~{selectedCharacters.size * 20} tokens)
                          </span>
                          <button
                            className="btn btn-primary"
                            onClick={handleRegenerateSelectedCharacters}
                            disabled={generatingCharacters || !hasEnoughTokens(selectedCharacters.size * 20)}
                            style={{
                              padding: '4px 12px',
                              fontSize: '12px',
                              fontWeight: '600',
                              background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                              opacity: (generatingCharacters || !hasEnoughTokens(selectedCharacters.size * 20)) ? 0.5 : 1,
                              cursor: (generatingCharacters || !hasEnoughTokens(selectedCharacters.size * 20)) ? 'not-allowed' : 'pointer'
                            }}
                          >
                            {generatingCharacters && generatingCharacterTotal > 0
                              ? `🔄 Regenerating ${(generatingCharacterIndex || 0) + 1}/${generatingCharacterTotal}...`
                              : `🔄 Regenerate Selected (${selectedCharacters.size})`}
                          </button>
                        </>
                      )}
                    </div>
                  )}
                  <div className="characters-list">
                    {structuredPlotData.characters?.map((char: any, idx: number) => {
                      const isGenerated = characterExists(char.name);
                      const isSelected = selectedCharacters.has(char.name);
                      return (
                        <div key={idx} className="character-card" style={{
                          border: isSelected ? '2px solid #f59e0b' : isGenerated ? '2px solid #10b981' : undefined,
                          background: isSelected ? 'rgba(245, 158, 11, 0.1)' : isGenerated ? 'rgba(16, 185, 129, 0.05)' : undefined,
                          boxShadow: isSelected ? '0 0 0 2px rgba(245, 158, 11, 0.2)' : undefined
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            {isGenerated && (
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => toggleCharacterSelection(char.name)}
                                style={{
                                  width: '16px',
                                  height: '16px',
                                  cursor: 'pointer',
                                  accentColor: '#f59e0b'
                                }}
                                title="Select for regeneration"
                              />
                            )}
                            <h4 style={{ margin: 0 }}>{char.name}</h4>
                            {isGenerated && <span style={{ color: '#10b981', fontSize: '14px' }}>✓</span>}
                          </div>
                          <textarea
                            value={char.description || ''}
                            onChange={(e) => handleUpdateCharacterDescription(idx, e.target.value)}
                            placeholder="Enter character description..."
                            style={{
                              width: '100%',
                              minHeight: '60px',
                              marginTop: '8px',
                              padding: '8px',
                              borderRadius: '4px',
                              border: '1px solid #ddd',
                              fontSize: '14px',
                              fontFamily: 'inherit',
                              resize: 'vertical',
                              lineHeight: '1.4'
                            }}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {!isSinglePageMode && (
                <div className="plot-section">
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '15px', flexWrap: 'wrap', gap: '10px' }}>
                    <h3 style={{ margin: 0 }}>🖼️ Backgrounds ({structuredPlotData.backgrounds?.length || 0})</h3>
                    {onGenerateBackground && structuredPlotData.backgrounds?.length > 0 && (() => {
                      const missingBgs = getMissingBackgrounds();
                      const tokensNeeded = missingBgs.length * 20;
                      const hasTokens = hasEnoughTokens(tokensNeeded);
                      const allGenerated = missingBgs.length === 0;
                      return (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          {!allGenerated && (
                            <span style={{ fontSize: '11px', color: hasTokens ? '#6B7280' : '#DC2626', fontWeight: '500' }}>
                              ~{tokensNeeded} tokens ({missingBgs.length} to generate)
                            </span>
                          )}
                          <button
                            className="btn btn-primary"
                            onClick={handleGenerateAllBackgrounds}
                            disabled={generatingBackgrounds || allGenerated || !hasTokens}
                            style={{
                              padding: '8px 16px',
                              fontSize: '14px',
                              fontWeight: '600',
                              background: allGenerated 
                                ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)' 
                                : 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                              opacity: (generatingBackgrounds || (!allGenerated && !hasTokens)) ? 0.5 : 1,
                              cursor: (generatingBackgrounds || allGenerated || !hasTokens) ? 'not-allowed' : 'pointer'
                            }}
                          >
                            {generatingBackgrounds 
                              ? `🖼️ Generating ${(generatingBackgroundIndex || 0) + 1}/${generatingBackgroundTotal}...`
                              : allGenerated 
                                ? '✅ All Generated' 
                                : `🖼️ Generate All Backgrounds (${missingBgs.length})`}
                          </button>
                        </div>
                      );
                    })()}
                  </div>
                  {/* Selection controls for regeneration */}
                  {onGenerateBackground && structuredPlotData.backgrounds?.some((bg: any) => backgroundExists(bg.name)) && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px', flexWrap: 'wrap' }}>
                      <button
                        onClick={selectedBackgroundsSet.size === structuredPlotData.backgrounds.filter((b: any) => backgroundExists(b.name)).length ? deselectAllBackgroundsForRegen : selectAllBackgroundsForRegen}
                        style={{
                          padding: '4px 10px',
                          fontSize: '11px',
                          fontWeight: '500',
                          background: 'transparent',
                          border: '1px solid #d1d5db',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          color: '#374151'
                        }}
                      >
                        {selectedBackgroundsSet.size === structuredPlotData.backgrounds.filter((b: any) => backgroundExists(b.name)).length ? '☐ Deselect All' : '☑ Select All'}
                      </button>
                      {selectedBackgroundsSet.size > 0 && (
                        <>
                          <span style={{ fontSize: '11px', color: '#6B7280' }}>
                            {selectedBackgroundsSet.size} selected
                          </span>
                          <span style={{ fontSize: '11px', color: hasEnoughTokens(selectedBackgroundsSet.size * 20) ? '#6B7280' : '#DC2626' }}>
                            (~{selectedBackgroundsSet.size * 20} tokens)
                          </span>
                          <button
                            className="btn btn-primary"
                            onClick={handleRegenerateSelectedBackgrounds}
                            disabled={generatingBackgrounds || !hasEnoughTokens(selectedBackgroundsSet.size * 20)}
                            style={{
                              padding: '4px 12px',
                              fontSize: '12px',
                              fontWeight: '600',
                              background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                              opacity: (generatingBackgrounds || !hasEnoughTokens(selectedBackgroundsSet.size * 20)) ? 0.5 : 1,
                              cursor: (generatingBackgrounds || !hasEnoughTokens(selectedBackgroundsSet.size * 20)) ? 'not-allowed' : 'pointer'
                            }}
                          >
                            {generatingBackgrounds && generatingBackgroundTotal > 0
                              ? `🔄 Regenerating ${(generatingBackgroundIndex || 0) + 1}/${generatingBackgroundTotal}...`
                              : `🔄 Regenerate Selected (${selectedBackgroundsSet.size})`}
                          </button>
                        </>
                      )}
                    </div>
                  )}
                  <div className="backgrounds-list">
                    {structuredPlotData.backgrounds?.map((bg: any, idx: number) => {
                      const isGenerated = backgroundExists(bg.name);
                      const isSelected = selectedBackgroundsSet.has(bg.name);
                      return (
                        <div key={idx} className="background-card" style={{
                          border: isSelected ? '2px solid #f59e0b' : isGenerated ? '2px solid #10b981' : undefined,
                          background: isSelected ? 'rgba(245, 158, 11, 0.1)' : isGenerated ? 'rgba(16, 185, 129, 0.05)' : undefined,
                          boxShadow: isSelected ? '0 0 0 2px rgba(245, 158, 11, 0.2)' : undefined
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            {isGenerated && (
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => toggleBackgroundSelection(bg.name)}
                                style={{
                                  width: '16px',
                                  height: '16px',
                                  cursor: 'pointer',
                                  accentColor: '#f59e0b'
                                }}
                                title="Select for regeneration"
                              />
                            )}
                            <h4 style={{ margin: 0 }}>{bg.name}</h4>
                            {isGenerated && <span style={{ color: '#10b981', fontSize: '14px' }}>✓</span>}
                          </div>
                          <textarea
                            value={bg.description || ''}
                            onChange={(e) => handleUpdateBackgroundDescription(idx, e.target.value)}
                            placeholder="Enter background description..."
                            style={{
                              width: '100%',
                              minHeight: '60px',
                              marginTop: '8px',
                              padding: '8px',
                              borderRadius: '4px',
                              border: '1px solid #ddd',
                              fontSize: '14px',
                              fontFamily: 'inherit',
                              resize: 'vertical',
                              lineHeight: '1.4'
                            }}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="plot-section">
                {!isSinglePageMode && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '15px', flexWrap: 'wrap', gap: '10px' }}>
                  <h3 style={{ margin: 0 }}>📄 Pages ({structuredPlotData.pages?.length || 0})</h3>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    {/* Selection controls */}
                    {structuredPlotData.pages?.length > 0 && (
                      <>
                        <button
                          onClick={selectedPages.size === structuredPlotData.pages.length ? deselectAllPages : selectAllPages}
                          style={{
                            padding: '6px 12px',
                            fontSize: '12px',
                            fontWeight: '500',
                            background: 'transparent',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            color: '#374151'
                          }}
                        >
                          {selectedPages.size === structuredPlotData.pages.length ? '☐ Deselect All' : '☑ Select All'}
                        </button>
                        {selectedPages.size > 0 && (
                          <span style={{ fontSize: '12px', color: '#6B7280', fontWeight: '500' }}>
                            {selectedPages.size} selected
                          </span>
                        )}
                      </>
                    )}
                    
                    {/* Generate Selected Pages button */}
                    {onGenerateAllPages && selectedPages.size > 0 && (() => {
                      const selectedTokens = getSelectedPagesTokens();
                      const hasTokensForSelected = hasEnoughTokens(selectedTokens);
                      return (
                        <>
                          <span style={{ fontSize: '11px', color: hasTokensForSelected ? '#6B7280' : '#DC2626', fontWeight: '500' }}>
                            ~{selectedTokens} tokens
                          </span>
                          <button
                            className="btn btn-primary"
                            onClick={async () => {
                              const pages = Array.from(selectedPages)
                                .sort((a, b) => a - b)
                                .map(idx => ({
                                  ...structuredPlotData.pages[idx],
                                  pageNumber: idx + 1
                                }));
                              await onGenerateAllPages(pages, { replaceExisting: true });
                              deselectAllPages(); // Clear selection after generation
                            }}
                            disabled={generatingPage !== null || !hasTokensForSelected}
                            style={{
                              padding: '8px 16px',
                              fontSize: '14px',
                              fontWeight: '600',
                              background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                              opacity: (!hasTokensForSelected || generatingPage !== null) ? 0.5 : 1,
                              cursor: (!hasTokensForSelected || generatingPage !== null) ? 'not-allowed' : 'pointer'
                            }}
                          >
                            🎯 Regenerate Selected ({selectedPages.size})
                          </button>
                        </>
                      );
                    })()}
                    
                    {/* Generate All Pages button */}
                    {onGenerateAllPages && structuredPlotData.pages?.length > 0 && (() => {
                      const totalTokens = structuredPlotData.pages.reduce((total: number, page: any) => total + estimatePageTokens(page), 0);
                      const hasTokensForAll = hasEnoughTokens(totalTokens);
                      return (
                        <>
                          <span style={{ fontSize: '11px', color: hasTokensForAll ? '#6B7280' : '#DC2626', fontWeight: '500' }}>
                            ~{totalTokens} tokens total {!hasTokensForAll && '(insufficient)'}
                          </span>
                          <button
                            className="btn btn-primary"
                            onClick={async () => {
                              const pages = structuredPlotData.pages.map((page: any, idx: number) => ({
                                ...page,
                                pageNumber: idx + 1
                              }));
                              await onGenerateAllPages(pages, { replaceExisting: true });
                            }}
                            disabled={generatingPage !== null || !hasTokensForAll}
                            style={{
                              padding: '8px 16px',
                              fontSize: '14px',
                              fontWeight: '600',
                              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                              opacity: (!hasTokensForAll || generatingPage !== null) ? 0.5 : 1,
                              cursor: (!hasTokensForAll || generatingPage !== null) ? 'not-allowed' : 'pointer'
                            }}
                          >
                            🚀 Regenerate All Pages
                          </button>
                        </>
                      );
                    })()}
                  </div>
                </div>
                )}
                <div className="pages-list">
                  {(isSinglePageMode 
                    ? [{ 
                        // In single page mode, ALWAYS show the page (even if no AI data yet)
                        // Use editedPageAiData which user can edit, allowing them to add prompts for pages without AI data
                        page: {
                          scenePrompt: editedPageAiData?.scenePrompt || '',
                          characterNames: editedPageAiData?.characterNames || [],
                          backgroundName: editedPageAiData?.backgroundName || '',
                          textBoxes: editedPageAiData?.textBoxes || []
                        },
                        idx: 0 // Index doesn't matter in single page mode
                      }]
                    : structuredPlotData.pages?.map((page: any, idx: number) => ({ page, idx })) || []
                  ).map(({ page, idx }: { page: any, idx: number }) => (
                    <div key={idx} className="page-card" style={{ 
                      border: selectedPages.has(idx) ? '2px solid #f59e0b' : undefined,
                      boxShadow: selectedPages.has(idx) ? '0 0 0 2px rgba(245, 158, 11, 0.2)' : undefined
                    }}>
                      <div className="page-header">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          {!isSinglePageMode && (
                          <input
                            type="checkbox"
                            checked={selectedPages.has(idx)}
                            onChange={() => togglePageSelection(idx)}
                            style={{
                              width: '18px',
                              height: '18px',
                              cursor: 'pointer',
                              accentColor: '#f59e0b'
                            }}
                            title="Select this page for batch generation"
                          />
                          )}
                          <h4 style={{ margin: 0 }}>Page {isSinglePageMode ? singlePageNumber : idx + 1}</h4>
                        </div>
                        {onGeneratePage && (() => {
                          const pageTokens = estimatePageTokens(page);
                          const hasTokens = hasEnoughTokens(pageTokens);
                          // In single-page mode, use the actual page number; otherwise use index
                          const pageNumberForGeneration = isSinglePageMode ? singlePageNumber! : idx + 1;
                          return (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span style={{ fontSize: '11px', color: hasTokens ? '#6B7280' : '#DC2626', fontWeight: '500' }}>
                                ~{pageTokens} tokens {!hasTokens && '(insufficient)'}
                              </span>
                              <button
                                className="btn btn-small btn-primary"
                                onClick={async () => {
                                  setGeneratingPage(idx);
                                  try {
                                    await onGeneratePage({
                                      ...page,
                                      pageNumber: pageNumberForGeneration
                                    }, { replaceExisting: true });
                                  } finally {
                                    setGeneratingPage(null);
                                  }
                                }}
                                disabled={generatingPage !== null || !hasTokens}
                                style={{
                                  opacity: (!hasTokens || generatingPage !== null) ? 0.5 : 1,
                                  cursor: (!hasTokens || generatingPage !== null) ? 'not-allowed' : 'pointer'
                                }}
                              >
                                {generatingPage === idx ? '⚡ Generating...' : '⚡ AI Generate Page'}
                              </button>
                            </div>
                          );
                        })()}
                        {!isSinglePageMode && (
                        <button
                          className="btn btn-small"
                          onClick={() => handleDeletePlotPage(idx)}
                          disabled={generatingPage !== null || (structuredPlotData.pages?.length || 0) <= 1}
                          style={{
                            padding: '4px 8px',
                            fontSize: '14px',
                            backgroundColor: '#fee2e2',
                            color: '#dc2626',
                            border: '1px solid #fecaca',
                            borderRadius: '4px',
                            cursor: (generatingPage !== null || (structuredPlotData.pages?.length || 0) <= 1) ? 'not-allowed' : 'pointer',
                            opacity: (generatingPage !== null || (structuredPlotData.pages?.length || 0) <= 1) ? 0.5 : 1
                          }}
                          title="Delete this page from the plot"
                        >
                          🗑️
                        </button>
                        )}
                      </div>
                      
                      <div className="page-details">
                        <div style={{ marginBottom: '10px' }}>
                          <strong>Background:</strong>
                          <select
                            value={page.backgroundName || ''}
                            onChange={(e) => handleUpdatePageBackground(idx, e.target.value)}
                            style={{
                              marginLeft: '8px',
                              padding: '4px 8px',
                              borderRadius: '4px',
                              border: '1px solid #ddd',
                              fontSize: '14px'
                            }}
                          >
                            <option value="">None</option>
                            {/* Combine backgrounds from plot and storybook, removing duplicates */}
                            {(() => {
                              const plotBgNames = new Set(structuredPlotData?.backgrounds?.map((bg: any) => bg.name) || []);
                              const allBackgrounds = [
                                ...(structuredPlotData?.backgrounds || []),
                                ...backgrounds.filter(bg => !plotBgNames.has(bg.name))
                              ];
                              return allBackgrounds.map((bg: any, bgIdx: number) => (
                                <option key={`bg-${bgIdx}-${bg.name}`} value={bg.name}>{bg.name}</option>
                              ));
                            })()}
                          </select>
                        </div>
                        <p><strong>Characters:</strong> {page.characterNames?.join(', ') || 'None'}</p>
                        <div className="scene-prompt" style={{ marginTop: '10px' }}>
                          <strong>Scene Prompt:</strong>
                          <textarea
                            value={page.scenePrompt || ''}
                            onChange={(e) => handleUpdateScenePrompt(idx, e.target.value)}
                            placeholder="Describe the scene composition and camera angle..."
                            style={{
                              width: '100%',
                              minHeight: '60px',
                              marginTop: '5px',
                              padding: '8px',
                              borderRadius: '4px',
                              border: '1px solid #ddd',
                              fontSize: '14px',
                              fontFamily: 'inherit',
                              resize: 'vertical'
                            }}
                          />
                        </div>
                        <div className="text-boxes" style={{ marginTop: '10px' }}>
                          <strong>Text Content:</strong>
                          {page.textBoxes?.map((tb: any, tbIdx: number) => (
                            <div key={tbIdx} className={`text-box ${tb.type}`} style={{ marginTop: '8px' }}>
                              {tb.type === 'dialogue' && tb.character && (
                                <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
                                  <strong>Character:</strong> {tb.character}
                                </div>
                              )}
                              <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
                                <strong>Type:</strong> {tb.type}
                              </div>
                              <textarea
                                value={tb.content}
                                onChange={(e) => handleUpdateTextBoxContent(idx, tbIdx, e.target.value)}
                                placeholder="Enter text content..."
                                style={{
                                  width: '100%',
                                  minHeight: '80px',
                                  padding: '8px',
                                  borderRadius: '4px',
                                  border: '1px solid #ddd',
                                  fontSize: '14px',
                                  fontFamily: 'inherit',
                                  resize: 'vertical'
                                }}
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Simple Plot List View */}
          {viewMode === 'simple' && (
            <div className="plots-list">
              {plots.map((plot) => (
                <div key={plot.pageNumber} className="plot-item">
                  <div className="plot-header">
                    <h4>Content Page {plot.pageNumber}</h4>
                    <div className="plot-actions">
                      <button
                        className="btn btn-small btn-secondary"
                        onClick={() => onAddToPage?.(currentPageIndex, plot.text, canvasWidth, canvasHeight)}
                        disabled={!plot.text.trim() || onAddToPage === undefined}
                        title="Add this plot text to the current page"
                      >
                        📄 Add to Page
                      </button>
                      <button
                          className="btn btn-small btn-secondary"
                          onClick={() => handleGeneratePage(plot.pageNumber)}
                          disabled={generatingPage !== null || !plot.text.trim()}
                          title="Improve this page's text with AI"
                        >
                          {generatingPage === plot.pageNumber ? '✨ Improving...' : '✨ AI Improve'}
                        </button>
                      <button
                        className="btn btn-small btn-secondary"
                        onClick={() => handleInsertPageBefore(plot.pageNumber)}
                        title="Insert a new page before this one"
                      >
                        +
                      </button>
                      <button
                        className="btn btn-small btn-secondary"
                        onClick={() => handleRemovePage(plot.pageNumber)}
                        disabled={plots.length <= 1}
                        title="Remove this page"
                      >
                        −
                      </button>
                    </div>
                  </div>
                  <textarea
                    value={plot.text}
                    onChange={(e) => handlePlotChange(plot.pageNumber, e.target.value)}
                    placeholder={`Enter plot/story text for content page ${plot.pageNumber}... (excludes Title cover and Back cover)`}
                    className="form-control plot-textarea"
                    rows={4}
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <div className="footer-left">
            <button 
              className="btn btn-danger" 
              onClick={handleClearAll}
              title="Clear all plot text"
            >
              🗑️ Clear All
            </button>
          </div>
          <div className="footer-right">
            <button className="btn btn-primary" onClick={handleClose}>
              Close & Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlotModal;
